"use client";

import TextLink from "@/app/components/atoms/TextLink";
import Icon from "@/app/components/atoms/Icon";
import { event } from "@/lib/analytics/gtag";
import type { ActionCardData } from "@/app/components/types";

/**
 * @component ActionCard
 * @description Clickable action card with a title, optional subtitle, and a directional arrow.
 * Renders as a button when `onClick` is provided, or a TextLink (Next.js Link) for hrefs.
 * @param title - Card title displayed in uppercase.
 * @param subtitle - Optional subtitle shown below the title.
 * @param href - Link destination (internal or external).
 * @param external - When true, opens link in a new tab.
 * @param onClick - Click handler (renders as button, e.g. to open a modal).
 * @param compact - When true, uses a compact row layout on md+ screens.
 * @param shadow - When true, adds shadow-l1 with shadow-l2 on hover (e.g. for ticket-style cards).
 * @returns A card element styled for dashboard quick-access actions.
 */
export default function ActionCard({
  title,
  subtitle,
  href,
  external,
  onClick,
  compact = false,
  disabled = false,
  shadow = false,
}: ActionCardData & { compact?: boolean; shadow?: boolean }) {
  const baseClass = `w-full p-4 md:p-6 border border-card-stroke flex text-left ${compact ? "flex-col justify-between items-start min-h-[90px]" : "flex-col justify-between items-start min-h-[140px] md:min-h-[180px]"}`;
  const shadowClass = shadow ? " shadow-l1 hover:shadow-l2 transition-shadow duration-200" : "";
  const cardClass = disabled
    ? `${baseClass}${shadowClass} bg-muted-bg cursor-default`
    : `${baseClass}${shadowClass} bg-white cursor-pointer hover:bg-hover hover:no-underline transition-colors`;

  const handleClick = () => {
    event('action_card_clicked', { category: 'navigation', label: title });
    onClick?.();
  };

  const content = (
    <>
      <div className="flex flex-col gap-1">
        <p className="text-title text-charcoal">{title}</p>
        {subtitle && (
          <p className={disabled ? "text-secondary-base text-charcoal" : "text-secondary-base text-stone"}>{subtitle}</p>
        )}
      </div>
      {!disabled && (
        <div className="self-end">
          <Icon name="arrow" className="w-6 h-6" />
        </div>
      )}
    </>
  );

  if (disabled) {
    return <div className={cardClass}>{content}</div>;
  }

  if (onClick) {
    return (
      <TextLink
        as="button"
        aria-label={`${title}${subtitle ? ` — ${subtitle}` : ""}`}
        className={cardClass}
        onClick={handleClick}
      >
        {content}
      </TextLink>
    );
  }

  return (
    <TextLink
      as="a"
      href={href ?? "#"}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      aria-label={`${title}${subtitle ? ` — ${subtitle}` : ""}`}
      className={cardClass}
      onClick={handleClick}
    >
      {content}
    </TextLink>
  );
}
